import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TagRegular } from '@fluentui/react-icons';
import { IProductAttribute } from '../../Api/Models/Products';
import { tagColor } from '../Helpers/ColorHelpers';

// Product tags and facet attributes on the detail pages, mirroring the chips on a product card but
// laid out as wrapping rows rather than image overlays. Clicking a chip leaves the detail page for a
// search filtered by it — the same affordance the cards give, expressed through the search URL
// (?tag=Value, ?facet=key|value), so no filter state has to be threaded through.

/** The product's place in the catalog tree, carried into search alongside an attribute filter. */
export interface IProductTaxonomy {
    segment?: string;
    category?: string;
    subCategory?: string;
}

/**
 * The taxonomy scope for an attribute-chip click, as deep as this product actually goes.
 *
 * Attribute facets are only resolvable within a category scope — the search page aggregates the
 * storefront's searchable attributes over the selected segment/category subset, and surfaces the facet
 * panel only once it has one. So a chip that carried nothing but `?facet=` landed on a search with the
 * filter applied and no panel to show it in, which reads as "my click did nothing".
 *
 * Every level the product has is sent, not just the deepest: the search page reads them independently,
 * and a sub-category without its category would leave the aggregation unscoped all over again. The
 * search page's own result-card chips already do this via updateFacetParams' categoryOverride; this is
 * the same idea reached from the detail page, where the full path is known rather than just a category.
 */
function taxonomyParams(taxonomy?: IProductTaxonomy): string {
    if (!taxonomy) return '';
    let qs = '';
    if (taxonomy.subCategory) qs += `&subcategory=${encodeURIComponent(taxonomy.subCategory)}`;
    if (taxonomy.category) qs += `&category=${encodeURIComponent(taxonomy.category)}`;
    if (taxonomy.segment) qs += `&segment=${encodeURIComponent(taxonomy.segment)}`;
    return qs;
}

interface IProductTagChipsProps {
    tags?: string[];
    style?: React.CSSProperties;
}

export const ProductTagChips: React.FC<IProductTagChipsProps> = (props) => {
    const navigate = useNavigate();
    const tags = (props.tags ?? []).filter(t => !!t && t.trim() !== '');
    if (tags.length === 0) return null;
    return (
        <div className='product-tags' style={props.style}>
            {tags.map((tag) => {
                const c = tagColor(tag);
                return (
                    <button
                        key={tag}
                        type='button'
                        className='product-tag-chip'
                        style={{ backgroundColor: c.background, color: c.color }}
                        title={`Filter by tag: ${tag}`}
                        onClick={() => navigate(`/search?tag=${encodeURIComponent(tag)}`)}
                    >
                        <TagRegular className='pc-tag-icon' />{tag}
                    </button>
                );
            })}
        </div>
    );
};

interface IProductAttributeChipsProps {
    attributes?: IProductAttribute[];
    style?: React.CSSProperties;
    /**
     * The product's catalog path. Without it the attribute filter arrives on the search page with
     * nothing to resolve against and no facet panel is rendered — see taxonomyParams.
     */
    taxonomy?: IProductTaxonomy;
}

export const ProductAttributeChips: React.FC<IProductAttributeChipsProps> = (props) => {
    const navigate = useNavigate();
    const attributes = (props.attributes ?? []).filter(a => !!a.key && !!a.value);
    if (attributes.length === 0) return null;
    return (
        <div className='product-attrs' style={props.style}>
            {attributes.map((attr, i) => (
                <button
                    key={`${attr.key}-${i}`}
                    type='button'
                    className='product-attr-chip'
                    title={`Filter by ${attr.key}: ${attr.value}`}
                    // The search page splits this param on the first '|', so the pair travels as one value.
                    onClick={() => navigate(
                        `/search?facet=${encodeURIComponent(`${attr.key}|${attr.value}`)}${taxonomyParams(props.taxonomy)}`)}
                >
                    <span className='product-attr-key'>{attr.key}:</span> {attr.value}
                </button>
            ))}
        </div>
    );
};
